export type FormDisplayTemplate = 'inline' | 'side-tab';

export interface FormDisplaySettings {
  displayTemplate?: FormDisplayTemplate;
  triggerLabel: string;
  showOnHomePage?: boolean;
  tabColor?: string;
}

export const DEFAULT_SIDE_TAB_LABEL = 'FORMU DOLDUR';
export const DEFAULT_TAB_COLOR = '#1a3326';
export const FORM_DISPLAY_META_ID = '__gts_form_display';

export function normalizeFormDisplayTemplate(value?: unknown): FormDisplayTemplate {
  return String(value || '').trim().toLowerCase() === 'side-tab' ? 'side-tab' : 'inline';
}

export function normalizeTabColor(value?: unknown): string {
  const raw = String(value || '').trim();
  return /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(raw) ? raw : '';
}

function isMetaField(item: unknown): item is Record<string, unknown> {
  return Boolean(item && typeof item === 'object' && String((item as Record<string, unknown>).id || '') === FORM_DISPLAY_META_ID);
}

function parseExtras(raw?: unknown): Pick<FormDisplaySettings, 'showOnHomePage' | 'tabColor'> {
  if (!raw) return {};
  try {
    const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
    if (!parsed || typeof parsed !== 'object') return {};
    const record = parsed as Record<string, unknown>;
    return {
      showOnHomePage: record.showOnHomePage === true,
      tabColor: normalizeTabColor(record.tabColor),
    };
  } catch {
    return {};
  }
}

function displayFromMeta(item?: Record<string, unknown>): FormDisplaySettings {
  const label = String(item?.label || '');
  const match = label.match(/^__gts_display:(.+)$/i);
  return {
    displayTemplate: normalizeFormDisplayTemplate(match?.[1] || item?.displayTemplate),
    triggerLabel: String(item?.placeholder || item?.triggerLabel || '').trim(),
    ...parseExtras(item?.helpText),
  };
}

export function unwrapFormFieldsJson(raw: unknown): { items: unknown[]; display: FormDisplaySettings } {
  const empty: FormDisplaySettings = { triggerLabel: '' };
  if (!raw) return { items: [], display: empty };

  try {
    const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
    if (Array.isArray(parsed)) {
      const meta = parsed.find(isMetaField);
      return {
        items: parsed.filter((item) => !isMetaField(item)),
        display: meta ? displayFromMeta(meta) : empty,
      };
    }
    if (parsed && typeof parsed === 'object') {
      const record = parsed as Record<string, unknown>;
      const items = (Array.isArray(record.fields) ? record.fields : []).filter((item) => !isMetaField(item));
      return {
        items,
        display: {
          displayTemplate: normalizeFormDisplayTemplate(record.displayTemplate),
          triggerLabel: String(record.triggerLabel || '').trim(),
          showOnHomePage: record.showOnHomePage === true,
          tabColor: normalizeTabColor(record.tabColor),
        },
      };
    }
  } catch {
    // ignore invalid json
  }

  return { items: [], display: empty };
}

export function serializeFormFieldsJson(
  fields: unknown[],
  display?: Partial<FormDisplaySettings>
): string {
  const cleanFields = (fields || []).filter((item) => !isMetaField(item));
  const displayTemplate = normalizeFormDisplayTemplate(display?.displayTemplate);
  const triggerLabel = String(display?.triggerLabel || '').trim();
  const showOnHomePage = display?.showOnHomePage === true;
  const tabColor = normalizeTabColor(display?.tabColor);

  if (displayTemplate === 'inline' && !triggerLabel && !showOnHomePage && !tabColor) {
    return JSON.stringify(cleanFields);
  }

  return JSON.stringify([
    {
      id: FORM_DISPLAY_META_ID,
      type: 'section',
      label: `__gts_display:${displayTemplate}`,
      placeholder: triggerLabel,
      helpText: JSON.stringify({ showOnHomePage, tabColor }),
    },
    ...cleanFields,
  ]);
}
